import React, { useState } from 'react'

import styled from '@emotion/styled'
import { AsYouType } from 'libphonenumber-js'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ExpandCenteredIcon } from '@arcade/web/icons/react'
import {
  ArcadeFieldLabel,
  FieldLabel,
  PadBox,
  PhoneInput as CDSPhoneInput,
  PhoneInputProps,
  RadioInputCard,
  spacing,
  Stack,
} from '@cash-design-system/react'
import { Country } from '@cash-web/protos/squareup/common/countries.pb'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-feature-flags'
import { Region, regionConfig } from '@cash-web/shared-data-access-region-provider'
import { ModalPortal } from '@cash-web/shared-feature-modal'
import { formatPhoneNumberE164 } from '@cash-web/shared-util-formatting'
import { isValidPhoneNumber } from '@cash-web/shared-util-validation'

import { FieldErrorText, Flag, FormInput } from '../'
import { FieldController } from '../FieldController/FieldController'

// Padding to maintain 1rem gap between region selector and input value
// given the varying widths of region selector with different region codes
const inputPaddingMap = {
  US: 123,
  GB: 136,
}

const InputContainer = styled.div<{ region?: SupportedRegion }>`
  position: relative;

  input {
    padding-left: ${({ theme, region }) => theme.pxToRem(region ? inputPaddingMap[region] : 16)};
  }
`

const RegionCodeAndPrefix = styled.button`
  position: absolute;
  height: 4rem;
  left: 1.125rem;
  display: flex;
  align-items: center;
  cursor: pointer;
  background-color: transparent;
  border: 0;
  padding: ${({ theme }) => theme.spacing.empty};
`
const Prefix = styled.div`
  margin: 0 ${({ theme }) => theme.spacing.xs};
  ${({ theme }) => theme.typography.input};
  color: ${({ theme }) => theme.colors.label};
`

const RegionItem = styled.div`
  display: flex;
  align-items: center;
  height: 4.5rem;
  padding: ${({ theme }) => theme.spacing.l};
  border: 2px solid transparent;
  border-radius: 1.5rem;
  background-color: ${({ theme }) => theme.colors.formFieldBackground};
  cursor: pointer;

  ${({ theme }) => theme.selectors.keyboard} &:focus {
    outline: 0.5rem solid ${({ theme }) => theme.addOpacity(theme.colors.primary, 0.2)};
  }

  &.selected {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const CountryName = styled.span`
  margin-left: ${({ theme }) => theme.spacing.m};
`

export const SUPPORTED_REGIONS = ['GB', 'US'] as const
type SupportedRegionTuple = typeof SUPPORTED_REGIONS
type SupportedRegion = SupportedRegionTuple[number]
export const isSupportedRegion = (region: Region): region is SupportedRegion =>
  SUPPORTED_REGIONS.includes(region as SupportedRegion)

export interface PhoneValue {
  regionCode: SupportedRegion
  phone: string
}

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
  label?: string
  onChange?: (value: PhoneValue) => void
  disableRegionCode?: boolean
  isLabelHidden?: boolean
} & Partial<PhoneInputProps>

export const PhoneInput = ({ placeholder, label, onChange, disableRegionCode, isLabelHidden, ...props }: Props) => {
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')
  const name = 'phone'
  const { t } = useTranslation()

  const regionDisplayName = {
    US: {
      displayName: t('United States', { context: 'label for phone number country' }),
    },
    GB: {
      displayName: t('United Kingdom', { context: 'label for phone number country' }),
    },
  }

  const {
    clearErrors,
    setValue,
    formState: { errors, isSubmitted, isSubmitting },
    watch,
  } = useFormContext()

  const regionCode = watch('regionCode')
  const supportedRegionCode = isSupportedRegion(regionCode) ? regionCode : 'US'
  const [showRegionCodeModal, setShowRegionCodeModal] = useState(false)

  const inputLabel = isLabelHidden
    ? ''
    : label || t('Mobile number', { context: 'label for form field asking for a user mobile number' }) || ''

  const handleCloseModal = () => setShowRegionCodeModal(false)

  const handleRegionCodeClick = (region: SupportedRegion) => {
    setValue('regionCode', region)
    setValue(name, '')
    clearErrors()
    // Wait 200 ms to close the modal, so that the user can see their newly selected choice briefly
    setTimeout(handleCloseModal, 200)
  }

  const errorMessage = t('Enter a valid phone number.', {
    context: 'error message for a form field with an invalid phone number',
  })

  const validate = (value: string) => {
    // try/catch is needed since formatPhoneNumberE164 can throw an Error for invalid inputs, such as length too short
    try {
      const formattedNumber = formatPhoneNumberE164(value, supportedRegionCode)
      return isValidPhoneNumber(formattedNumber, supportedRegionCode) || errorMessage
    } catch {
      return errorMessage
    }
  }

  const sortedRegions = [...SUPPORTED_REGIONS].sort()

  if (isArcadeMigrationEnabled) {
    return (
      <>
        <ArcadeFieldLabel label={inputLabel} htmlFor={name}>
          <FieldController
            FieldComponent={CDSPhoneInput}
            componentProps={{
              ...props,
              'aria-label': t('Mobile number', { context: 'label of a form field asking for a user mobile number' }),
              showRegionCode: !disableRegionCode,
              placeholder: t('Enter phone number', { context: 'Phone number input placeholder' }),
              regionCode: supportedRegionCode,
              regionCodeAriaLabel: t('Country calling code', {
                context: 'button to switch the prefix of their phone number',
              }),
              onClickRegionCode: () => setShowRegionCodeModal(true),
            }}
            name={name}
            rules={{
              required: {
                value: true,
                message: errorMessage,
              },
              validate,
            }}
          />
        </ArcadeFieldLabel>

        {showRegionCodeModal && (
          <ModalPortal onClose={handleCloseModal}>
            <PadBox padding={['s', 'empty', 'empty', 'empty']}>
              <Stack space={spacing.m}>
                {sortedRegions.map(region => (
                  <RadioInputCard
                    label={`${regionDisplayName[region].displayName} (+${regionConfig[region].phonePrefix})`}
                    checked={supportedRegionCode === region}
                    onClick={() => handleRegionCodeClick(region)}
                    name="regionCode"
                    key={region}
                  />
                ))}
              </Stack>
            </PadBox>
          </ModalPortal>
        )}
      </>
    )
  }

  const phone = watch(name)
  const handleTransformNumber = (value: string) => {
    let maskedNumber = ''
    // Don't allow typing a plus sign for international prefixes, since the prefix
    // is selected by the dropdown
    const number = value.replace(/\+/, '')

    // If you type three numbers, it gets formatted automatically to (555). However,
    // if you backspace, the ) gets removed, but then immediately added back by AsYouType
    // This check makes it so that backspacing removes the 3rd number and not just the )
    // E.g. backspacing (555) changes the input value to 55
    // See https://github.com/catamphetamine/libphonenumber-js/issues/225 for more details
    if (supportedRegionCode === Country.US && number.includes('(') && !number.includes(')')) {
      const numberWithoutParens = number.replace('(', '')
      maskedNumber = numberWithoutParens.slice(0, numberWithoutParens.length - 1)
    } else {
      maskedNumber = new AsYouType(supportedRegionCode).input(number)
    }

    return maskedNumber
  }

  const defaultPlaceholder =
    supportedRegionCode === Country.GB
      ? t('1234 567890', { context: 'placeholder for form field asking for a user mobile number' })
      : t('(123) 456-7890', { context: 'placeholder for form field asking for a user mobile number' })
  const inputPlaceholder = placeholder || defaultPlaceholder

  const error = errors[name]
  const showError = error && (error?.type !== 'required' || isSubmitted)

  const isDisabled = isSubmitting

  const countryPrefix = `+${regionConfig[supportedRegionCode].phonePrefix}`

  return (
    <div>
      <FieldLabel label={inputLabel} htmlFor={name}>
        <InputContainer region={disableRegionCode ? undefined : supportedRegionCode}>
          {disableRegionCode ? null : (
            <RegionCodeAndPrefix
              onClick={() => {
                if (!isDisabled) {
                  setShowRegionCodeModal(true)
                }
              }}
              type="button"
              aria-label={t('Country calling code', { context: 'button to switch the prefix of their phone number' })}
              aria-description={t('Country prefix is currently {countryPrefix}, open this to change your prefix', {
                context: 'description for the button to switch the prefix of their phone number',
                countryPrefix: countryPrefix,
              })}
            >
              <Flag countryCode={supportedRegionCode as Region} />
              <Prefix className="notranslate">{countryPrefix}</Prefix>
              <ExpandCenteredIcon variant={showError ? 'danger' : 'success'} size={24} />
            </RegionCodeAndPrefix>
          )}
          <FormInput
            aria-label={t('Mobile number', { context: 'label of a form field asking for a user mobile number' })}
            {...props}
            id={name}
            placeholder={inputPlaceholder}
            name={name}
            rules={{
              required: {
                message: errorMessage,
                value: true,
              },
              validate,
            }}
            transform={handleTransformNumber}
            // TODO: Remove showErrors when Arcade migration is complete: https://block.atlassian.net/browse/CASHW-4763
            showErrors={false}
            value={phone}
            type="tel"
            inputMode="numeric"
          />
        </InputContainer>
      </FieldLabel>
      {!isArcadeMigrationEnabled && <FieldErrorText name={name} />}
      {showRegionCodeModal && (
        <ModalPortal onClose={handleCloseModal}>
          <Stack space={spacing.m} as="ul">
            {sortedRegions.map(region => (
              <li key={region}>
                <RegionItem
                  role="button"
                  tabIndex={0}
                  className={supportedRegionCode === region ? 'selected' : ''}
                  onClick={() => handleRegionCodeClick(region)}
                  onKeyDown={e => (e.key === 'Enter' || e.code === 'Space') && handleRegionCodeClick(region)}
                >
                  <Flag countryCode={region as Region} />
                  <CountryName>
                    {/* TODO need to include all string parts in localized string. Also, remove string concatenation. */}
                    {regionDisplayName[region].displayName} (+{regionConfig[region].phonePrefix})
                  </CountryName>
                </RegionItem>
              </li>
            ))}
          </Stack>
        </ModalPortal>
      )}
    </div>
  )
}
